/**
 * Theme Utility Functions
 *
 * Provides helper functions for theme management
 */

/**
 * Apply the current theme to the document
 * This ensures the theme is applied immediately without waiting for next-themes
 */
export const applyTheme = (theme: string | null): void => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;

    // Get the theme from localStorage or use the provided theme
    const currentTheme = theme || localStorage.getItem('theme-preference') || 'light';

    // Apply the theme class
    if (currentTheme === 'dark') {
        root.classList.add('dark');
        root.classList.remove('light');
    } else {
        root.classList.remove('dark');
        root.classList.add('light');
    }
};

/**
 * Initialize theme on page load
 * This should be called as early as possible in the application lifecycle
 */
export const initializeTheme = (): void => {
    if (typeof window === 'undefined') return;

    // Apply the theme from localStorage
    applyTheme(null);

    // Add event listener for storage changes
    window.addEventListener('storage', (event) => {
        if (event.key === 'theme-preference') {
            applyTheme(event.newValue);
        }
    });
};

/**
 * Toggle between light and dark themes
 */
export const toggleTheme = (): void => {
    if (typeof window === 'undefined') return;

    const currentTheme = localStorage.getItem('theme-preference') || 'light';
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

    localStorage.setItem('theme-preference', newTheme);
    applyTheme(newTheme);

    // Dispatch storage event to notify other tabs
    window.dispatchEvent(
        new StorageEvent('storage', {
            key: 'theme-preference',
            newValue: newTheme,
        }),
    );
};
